import h from 'hyperscript';

import { addStyleClass } from '@disy/lib/add-style-class';
import { array } from '@disy/lib/array-util';
import type { Content } from '@disy/ui/content';
import { vr } from '@disy/ui/separator';

import { createResourceLabel } from 'ui/resource-label/resource-label';

import { getDataSourceFlavorOrDataSourceTypeAsText } from 'cadenza/admin/datasource-filter/datasource-flavor-or-datasource-type-utils';
import type { DataSourceFlavor } from 'cadenza/repository/data-source-flavor';
import type { DataSourceType } from 'cadenza/repository/model/data-source-type.gen';

import './datasource-tile.css';

const COMPONENT_NAME = 'd-datasource-tile';

/**
 * The DisplayMode interface describes how the component is displayed
 * singleLine - all details in one row
 * textWithIcon - displays only the text passed as the name, no other parts of the components are rendered
 * onlyText - displays only the text passed as the name and also does not have an icon for the resource type
 * complete - displays all the details of the component in 2 rows
 */
export type DisplayMode = 'complete' | 'singleLine' | 'textWithIcon' | 'onlyText';

/**
 * The DatasourceTileOptions interface provides optional details about the DatasourceTile component look
 *
 * @param repositoryName - repository name (rendered when present and DisplayMode='complete'
 * @param displayMode - the display mode
 */
interface DataSourceTileOptions {
  repositoryName?: string;
  displayMode?: DisplayMode;
  styleClass?: string | string[];
}

type DataSourceName = Content;

/**
 * The DatasourceTile is a component to show the details of a datasource.
 * It can be used in four modes:
 * - datasource name with type (for not RDBMS datasources) or with db flavor (for RDBMS datasources) and repository name
 * - datasource name with type (for not RDBMS datasources) or with db flavor (for RDBMS datasources)
 * - datasource name with type (for not RDBMS datasources) or with db flavor (for RDBMS datasources) in one line
 * - custom text (passed as datasource name) only with no other information (used for example to display "all datasources" in select component)
 *
 * @param name - datasource name/custom text or node(s) (when matching text is highlighted)
 * @param type - datasource type
 * @param flavor - database flavor
 * @param options - additional data depending on use case
 * @param options.repositoryName - repository name
 * @param options.displayMode - display mode
 * @return the component
 */
export function createDataSourceTile(
  name: DataSourceName,
  type: DataSourceType,
  flavor?: DataSourceFlavor,
  options?: DataSourceTileOptions,
) {
  return new DatasourceTile(name, getDataSourceFlavorOrDataSourceTypeAsText(type, flavor), options);
}

class DatasourceTile extends HTMLElement {
  constructor(name: DataSourceName, dataSourceTypeOrFlavor: string, options: DataSourceTileOptions = {}) {
    super();
    const { displayMode = 'complete', repositoryName, styleClass } = options;
    this.classList.add(COMPONENT_NAME);
    addStyleClass(this, styleClass);

    this.setAttribute('name', h('div', name).textContent!);
    this.setAttribute('display-mode', displayMode);
    if (repositoryName) {
      this.setAttribute('repository-name', repositoryName);
    }

    const resourceLabelStyleClass = [`${COMPONENT_NAME}--datasource-name`, 'ellipsis'];
    if (this.displayMode === 'onlyText') {
      // only the main text
      this.append(h(`span.${COMPONENT_NAME}--datasource-name.ellipsis`, name));
    } else if (this.displayMode === 'textWithIcon') {
      // only the main text
      this.append(createResourceLabel('datasource', name, { styleClass: resourceLabelStyleClass }));
    } else if (this.displayMode === 'singleLine') {
      this.append(
        createResourceLabel('datasource', [...array(name), ` (${dataSourceTypeOrFlavor})`], {
          styleClass: resourceLabelStyleClass,
        }),
      );
    } else {
      // complete component
      const typeAndRepositoryName =
        this.repositoryName ?
          [h('label', dataSourceTypeOrFlavor), vr(), createResourceLabel('repository', this.repositoryName)]
        : h('label', dataSourceTypeOrFlavor);
      this.append(
        createResourceLabel('datasource', name, { styleClass: resourceLabelStyleClass }),
        h('.d-stack-h .space-1', typeAndRepositoryName),
      );
    }
  }

  get name() {
    return this.getAttribute('name') as string;
  }

  get repositoryName() {
    return this.getAttribute('repository-name');
  }

  get displayMode() {
    return this.getAttribute('display-mode');
  }
}

customElements.define(COMPONENT_NAME, DatasourceTile);
